import React from 'react';
import Metric from 'utils/Metric'
import currencyConverter from 'utils/currencyConverter'
import getCurrencyDetails from 'utils/getCurrencyDetails'

const getTickProps = (oldValue) => {
  let value = oldValue;
  let suffix = "";
  let roundTo = 0;
  let decimal = 0;
  if (value === 0) {
    suffix = "";
    roundTo = 0;
  } else if (value >= 100 && value < 1000) {
    suffix = "";
    roundTo = 1;
  } else if (value >= 1000 && value < 1000000) {
    suffix = "k"
    roundTo = 100;
    if (value < 10000) {
      decimal = parseFloat(Math.round(value).toString().split("").reverse()[2]);
    }
  } else if (value >= 1000000 && value < 1000000000) {
    suffix = "m"
    roundTo = 100;
    if (value < 10000000) {
      decimal = parseFloat(Math.round(value).toString().split("").reverse()[5]);
    }
  } else if (value >= 1000000000) {
    suffix = "b"
    roundTo = 100;
    if (value < 10000000000) {
      decimal = parseFloat(Math.round(value).toString().split("").reverse()[8]);
    }
  }

  if (roundTo > 0) {
    value = Math.round(value / roundTo) * roundTo;
  }
  //turn into the last digits after multiples of 3 e.g 1k 100k 999k 89k instead of 1000 999000 89000
  if (value >= 100) {
    value = parseFloat(value.toString().substring(0, (value.toString().length % 3) == 0 ? 3 : (value.toString().length % 3)));
  }
  // We want to show partial values for some abbreviated ticks to avoid showing duplicates, like some were saying 1k, 1k, 1k, 1k, then jump to 2k, 2k, 2k, 2k
  if (decimal > 0) {
    value += decimal / 10
  }

  return {
    value: value,
    decimal: decimal,
    suffix: suffix,
  }
}

const MetricFormatter = {
  YY: (props) => {
    if (isNaN(props.value) || (typeof props.value == "string")) {
      return (
        <Metric value={0} commas={true} precision={2} suffix="%" />
      )
    } else {
      return (
        <Metric value={props.value} commas={true} precision={2} suffix="%" />
      )
    }
  },
  YY0: (props) => <Metric value={props.value} commas={true} precision={0} suffix="%" />,
  Sessions: (props) => <Metric value={props.value} commas={true} precision={0} />,
  Impressions: (props) => <Metric value={props.value} commas={true} precision={0} />,
  PhoneCalls: (props) => <Metric value={props.value} commas={true} precision={0} />,
  SearchVisibility: (props) => <Metric value={props.value} commas={true} precision={0} suffix="%" />,
  OrganicTraffic: (props) => <Metric value={props.value} commas={true} precision={0} suffix="" />,
  Clicks: (props) => <Metric value={props.value} commas={true} precision={0} suffix="" />,
  Revenue: (props) => {
    let currencyDetails = getCurrencyDetails(props.displayCurrency);
    return (
      <Metric 
        baseCurrency={props.baseCurrency}
        displayCurrency={props.displayCurrency}
        prefix={"$"}
        value={props.value}
        commas={true}
        precision={0}
        suffix={currencyDetails.suffix}
      />
    )
  },
  BounceRate: (props) => <Metric prefix="" value={props.value} commas={true} precision={2} suffix="%" />,
  ROAS: (props) => {
    let currencyDetails = getCurrencyDetails(props.displayCurrency);
    return (
      <Metric 
        client={props.client}
        prefix={currencyDetails.prefix}
        value={props.value}
        commas={true}
        precision={2}
        suffix={currencyDetails.suffix}
      />
    )
  },
  AOV: (props) => {
    let currencyDetails = getCurrencyDetails(props.displayCurrency);
    return (
      <Metric
        baseCurrency={props.baseCurrency}
        displayCurrency={props.displayCurrency}
        prefix={currencyDetails.prefix}
        value={props.value}
        commas={true}
        precision={2}
        suffix={currencyDetails.suffix}
      />
    )
  },
  CPA: (props) => {
    let currencyDetails = getCurrencyDetails(props.displayCurrency);
    return (
      <Metric
        baseCurrency={props.baseCurrency}
        displayCurrency={props.displayCurrency}
        prefix={currencyDetails.prefix}
        value={props.value}
        commas={true}
        precision={2}
        suffix={currencyDetails.suffix}
      />
    )
  },
  CPC: (props) => {
    let currencyDetails = getCurrencyDetails(props.displayCurrency);
    return (
      <Metric
        baseCurrency={props.baseCurrency}
        displayCurrency={props.displayCurrency}
        prefix={currencyDetails.prefix}
        value={props.value}
        commas={true}
        precision={2}
        suffix={currencyDetails.suffix}
      />
    )
  },
  CVR: (props) => <Metric prefix="" value={props.value} commas={true} precision={2} suffix="%" />,
  CTR: (props) => <Metric prefix="" value={props.value} commas={true} precision={2} suffix="%" />,
  Conversions: (props) => <Metric prefix="" value={props.value} commas={true} precision={0} suffix="" />,
  Backlinks: (props) => <Metric prefix="" value={props.value} commas={true} precision={0} suffix="" />,
  DefaultTick: (props) => {
    let tickProps = getTickProps(props.value);
    let suffix = tickProps.suffix;
    let decimal = tickProps.decimal;
    let value = tickProps.value;
    return (
      <Metric prefix="" value={value} commas={false} precision={decimal > 0 ? 1 : 0} suffix={suffix} />
    )
  },
  MonetaryTick: (props) => {
    let currencyDetails = getCurrencyDetails(props.displayCurrency)
    let convertedValue = currencyConverter(props.value, props.baseCurrency, props.displayCurrency)
    let tickProps = getTickProps(convertedValue);
    let suffix = tickProps.suffix;
    let decimal = tickProps.decimal;
    let value = tickProps.value;
    return (
      <Metric 
        prefix={currencyDetails.prefix} 
        value={value} 
        commas={false} 
        precision={decimal > 0 ? 1 : 0} 
        suffix={currencyDetails.suffix + suffix} 
      />
    )
  },
  CalculatedTick: (props) => {
    let tickProps = getTickProps(props.value);
    let suffix = tickProps.suffix;
    let decimal = tickProps.decimal;
    let value = tickProps.value;
    return (
      <Metric prefix="" value={value} commas={false} precision={value < 100 ? 2 : decimal > 0 ? 1 : 0} suffix={suffix} />
    )
  },
  CalculatedMonetaryTick: (props) => {
    let currencyDetails = getCurrencyDetails(props.displayCurrency)
    let convertedValue = currencyConverter(props.value, props.baseCurrency, props.displayCurrency)
    let tickProps = getTickProps(convertedValue);
    let suffix = tickProps.suffix;
    let decimal = tickProps.decimal;
    let value = tickProps.value;
    return (
      <Metric
        prefix={currencyDetails.prefix}
        value={value}
        commas={false}
        precision={value < 100 ? 2 : decimal > 0 ? 1 : 0}
        suffix={currencyDetails.suffix + suffix}
      />
    )
  }
}

export default MetricFormatter;
